import React, { useState, useRef, useEffect } from "react";
import { Col, Row, Card, Breadcrumb, Badge, Form } from 'react-bootstrap';
import { useParams, useNavigate, Link } from "react-router-dom";
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import '../../assets/css/new_boards.css';
import CustomConfirmPopup from "../../components/CustomConfirmPopup";
import { Avatar } from 'primereact/avatar';
import { AvatarGroup } from 'primereact/avatargroup';
import { Tooltip } from 'primereact/tooltip';
import axios from "axios";
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import CustomToast from '../../components/CustomToast';
import { OverlayPanel } from 'primereact/overlaypanel';
import AddBoardMembersDialog from "./AddBoardMembersDialog";
import { getSocket } from '../../context/socket'; // adjust the path if needed
import nouserImg from '../../assets/images/no_user.png'; // Placeholder for missing profile images
import { IoHandRightSharp } from "react-icons/io5";
import { FaSkullCrossbones } from "react-icons/fa";
import { CgFlagAlt } from "react-icons/cg";
import { Dialog } from 'primereact/dialog';
import CardReviewQueue from '../../components/CardReviewQueue';
import CardTaskDialogContent from '../../components/CardTaskDialogContent';

const Boards = () => {
    const { accessToken, userId } = useAuth(); // Get the access token from the context
    const op = useRef(null);
    const socket = getSocket();
    const navigate = useNavigate(); // React Router's navigate hook
    const toastRef = useRef(null);

    const showMessage = (severity, detail) => {
        const summary = severity.charAt(0).toUpperCase() + severity.slice(1);
        toastRef.current.show(severity, summary, detail);
    };
    const [visibleRight, setVisibleRight] = useState(false);
    const [boards, setBoards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('recent');
    const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);


    const [closedboards, setClosedBoards] = useState([]);
    const [closedloading, setClosedLoading] = useState(false);
    const [closedsearchTerm, setClosedSearchTerm] = useState('');
    const [closedsortOrder, setClosedSortOrder] = useState('recent');
    const [debouncedClosedSearch, setDebouncedClosedSearch] = useState(closedsearchTerm);

    const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
    const [taskDialogVisible, setTaskDialogVisible] = useState(false);
    const [selectedCardId, setSelectedCardId] = useState(null);
    const [selectedBoardId, setSelectedBoardId] = useState(null);
    const [selectedBoardData, setSelectedBoardData] = useState({ members: [], board_id: null });
    const [userCards, setUserCards] = useState([]);
    const [reviewQueueTick, setReviewQueueTick] = useState(0);
    const [pendingReviewCount, setPendingReviewCount] = useState(0);
    const [boardColumnIssues, setBoardColumnIssues] = useState([]);
    const [unassignedBoardIssues, setUnassignedBoardIssues] = useState([]);
    const [totalUnassignedCards, setTotalUnassignedCards] = useState(0);


    const fetchBoards = () => {
        setLoading(true);
    
        const handleBoardsData = (response) => {
            socket.off('fetch-boards-response', handleBoardsData); // Clean after handling
    
            let fetchedBoards = response.boards || [];

            // Sorting logic
            if (sortOrder === 'recent') {
                // Newest first
                fetchedBoards.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
            } else if (sortOrder === 'least') {
                // Oldest first
                fetchedBoards.sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt));
            }
             else if (sortOrder === 'az') {
                fetchedBoards.sort((a, b) => a.name.localeCompare(b.name));
            } else if (sortOrder === 'za') {
                fetchedBoards.sort((a, b) => b.name.localeCompare(a.name));
            }
    
            setBoards(fetchedBoards);
            setLoading(false);
        };
    
        // Attach before emit
        socket.off('fetch-boards-response', handleBoardsData);
        socket.on('fetch-boards-response', handleBoardsData);
    
        // Now emit
        socket.emit('fetch-boards', {
            userId: userId,
            board_name: debouncedSearch || ''
        });
    };

    useEffect(() => {
        fetchBoards();
    }, [debouncedSearch, sortOrder]);


    useEffect(() => {
        const handleBoardsUpdated = () => {
            fetchBoards(); // Re-fetch on boards-updated
        };

        socket.on('boards-updated', handleBoardsUpdated);

        return () => {
            socket.off('boards-updated', handleBoardsUpdated);
        };
    }, []);



    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);



    const handleReviewAction = () => {
        setReviewQueueTick((t) => t + 1);
        if (socket && userId) {
            socket.emit('fetchCardsForUser', { user_id: userId });
        }
    };

    const openReviewCard = ({ cardId, boardId }) => {
        if (boardId) {
            navigate(`/Graphe/dashboard/kanbanboard/${boardId}?card=${cardId}`);
            return;
        }
        openCardDetails({ card_id: cardId, list: { board: { board_id: boardId } } });
    };

    const openCardDetails = (card) => {
        setSelectedCardId(card.card_id);
        setSelectedBoardId(card.list?.board?.board_id ?? null);
        setTaskDialogVisible(true);
    };

    const handleTaskDialogClose = () => {
        setTaskDialogVisible(false);
        setSelectedCardId(null);
        setSelectedBoardId(null);
    };



    const handleCloseBoard = async (board_id, is_closed) => {
        setLoading(true);
        try {
            const response = await axios.put(
                `${config.apiBASEURL}/TaskBoardRoutes/update-board-status`,
                { board_id, user_id: userId, is_closed }, // assuming userId is available in state/props
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );

            showMessage('success', response.data.message);
            fetchBoards(); // Refresh board list after update
            fetchClosedBoards(); // Refresh closed board list after update
        } catch (error) {
            showMessage('error', error?.response?.data?.message || 'Failed to update board status');
        }
        setLoading(false);
    };



    const fetchClosedBoards = async () => {
        setClosedLoading(true);
        try {
            const response = await axios.get(
                `${config.apiBASEURL}/TaskBoardRoutes/fetch-Closed-boards/${userId}`,
                {
                    headers: { Authorization: `Bearer ${accessToken}` },
                    params: searchTerm ? { board_name: searchTerm } : {},
                }
            );
            let fetchedBoards = response.data.boards || [];

            // Sorting Logic
            if (sortOrder === 'recent') {
                fetchedBoards.sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt));
            } else if (sortOrder === 'least') {
                fetchedBoards.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
            } else if (sortOrder === 'az') {
                fetchedBoards.sort((a, b) => a.name.localeCompare(b.name));
            } else if (sortOrder === 'za') {
                fetchedBoards.sort((a, b) => b.name.localeCompare(a.name));
            }


            setClosedBoards(fetchedBoards);
        } catch (error) {
            showMessage('error', error?.response?.data?.message || 'Failed to fetch boards');
        } finally {
            setClosedLoading(false);
        }
    };

    useEffect(() => {
        fetchClosedBoards();
    }, [debouncedClosedSearch, accessToken, userId, closedsortOrder]);


    useEffect(() => {
        const handler = setTimeout(() => {
            setClosedSearchTerm(closedsearchTerm);
        }, 500); // 500ms debounce delay

        return () => {
            clearTimeout(handler);
        };
    }, [closedsearchTerm]);

    const handleAddMember = (event, board) => {
        setSelectedBoardData({
            members: board.members || [],
            board_id: board.board_id
        });
        op.current.toggle(event); // Open or toggle the overlay panel
    };

    useEffect(() => {
        // Listen for updates when board members are modified
        socket.on('boardMembersUpdated', ({ board_id, members }) => {
            fetchBoards(); // Re-fetch the boards to reflect latest members
        });

        // Clean up the socket event when component unmounts
        return () => {
            socket.off('boardMembersUpdated');
        };
    }, []);


    useEffect(() => {
        if (!accessToken || !userId) return;
        axios
            .get(`${config.apiBASEURL}/TaskBoardRoutes/boards/review-column-health/${userId}`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            })
            .then((response) => {
                setBoardColumnIssues(response.data?.needs_attention || []);
            })
            .catch(() => setBoardColumnIssues([]));

        axios
            .get(`${config.apiBASEURL}/TaskBoardRoutes/boards/unassigned-cards/${userId}`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            })
            .then((response) => {
                setUnassignedBoardIssues(response.data?.needs_attention || []);
                setTotalUnassignedCards(response.data?.total_unassigned || 0);
            })
            .catch(() => {
                setUnassignedBoardIssues([]);
                setTotalUnassignedCards(0);
            });
    }, [accessToken, userId]);

    useEffect(() => {
        if (!socket || !userId) return;

        // Emit to fetch cards for this user
        socket.emit("fetchCardsForUser", { user_id: userId });

        // Listen for the response
        socket.on("userCardsFetched", ({ cards }) => {
            setUserCards(cards); // store in state
        });

        socket.on("fetchUserCardsError", ({ message }) => {
            showMessage("error", message || "Failed to fetch user cards.");
        });

        // Cleanup on unmount
        return () => {
            socket.off("userCardsFetched");
            socket.off("fetchUserCardsError");
        };
    }, [socket, userId]);


    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const options = { day: 'numeric', month: 'short' }; // e.g., "7 Apr"
        return date.toLocaleDateString('en-US', options);
    };

    const getDueDateClass = (dueDate) => {
        if (!dueDate) return "";

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const due = new Date(dueDate);
        due.setHours(0, 0, 0, 0);

        const diffDays = (due - today) / (1000 * 60 * 60 * 24);

        if (diffDays < 0) {
            return "bg-danger"; // Passed
        } else if (diffDays === 0) {
            return "bg-danger"; // Today
        } else if (diffDays === 1) {
            return "bg-warning"; // Tomorrow
        } else {
            return "bg-success"; // Future
        }
    };



    return (
        <>
            <Row className='body_content'>
                <Row className='mx-0'>
                    <Col md={4} lg={3} className='mb-4'>
                        <Breadcrumb>
                            <Breadcrumb.Item onClick={() => navigate(-1)} style={{ cursor: "pointer" }}><i className="pi pi-angle-left"></i>Back</Breadcrumb.Item>
                            <Breadcrumb.Item active>
                                Boards
                                {pendingReviewCount > 0 && (
                                    <Badge bg="warning" text="dark" className="ms-2">
                                        {pendingReviewCount} to review
                                    </Badge>
                                )}
                            </Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>
                    <Col md={8} lg={9} className='mb-4 d-flex justify-content-end align-items-center gap-2'>
                        <Button
                            icon="pi pi-calendar"
                            onClick={() => navigate('/Graphe/dashboard/user_gantt')}
                            label="My Timeline"
                            className="border-0 py-2"
                            severity="info"
                        />
                        <Button
                            icon="pi pi-chart-bar"
                            onClick={() => navigate('/Graphe/dashboard/admin_gantt')}
                            label="Project Timeline"
                            className="border-0 py-2"
                            severity="secondary"
                        />
                        <Button
                            icon="pi pi-users"
                            onClick={() => navigate('/Graphe/dashboard/admin_user_gantt')}
                            label="User Timeline"
                            className="border-0 py-2"
                            severity="secondary"
                        />
                        <Button
                            icon="pi pi-chart-line"
                            onClick={() => navigate('/Graphe/dashboard/capacity_dashboard')}
                            label="Capacity"
                            className="border-0 py-2"
                            severity="help"
                        />
                        <Button
                            icon="pi pi-inbox"
                            onClick={() => navigate('/Graphe/dashboard/schedule_approvals')}
                            label="Approvals"
                            className="border-0 py-2"
                            severity="warning"
                        />
                        <Button
                            icon="pi pi-arrow-left"
                            onClick={() => setVisibleRight(true)}
                            label="Closed Boards"
                            className="border-0 py-2"
                            severity="danger"
                        />
                    </Col>
                    {boardColumnIssues.length > 0 && (
                        <Col md={12} lg={12} className="mb-3 px-3">
                            <div className="alert alert-warning py-2 mb-0 small">
                                <strong>Review columns missing on {boardColumnIssues.length} board(s):</strong>{' '}
                                {boardColumnIssues.map((board) => (
                                    <span key={board.board_id} className="me-3">
                                        {board.board_name} — add lists:{' '}
                                        {board.missing
                                            .map((key) =>
                                                key === 'inReview'
                                                    ? '"In Review"'
                                                    : key === 'inChanges'
                                                      ? '"In Changes"'
                                                      : '"Completed"'
                                            )
                                            .join(', ')}
                                    </span>
                                ))}
                            </div>
                        </Col>
                    )}
                    {unassignedBoardIssues.length > 0 && (
                        <Col md={12} lg={12} className="mb-3 px-3">
                            <div className="alert alert-warning py-2 mb-0 small">
                                <strong>
                                    {totalUnassignedCards} unassigned task
                                    {totalUnassignedCards === 1 ? '' : 's'} on{' '}
                                    {unassignedBoardIssues.length} board
                                    {unassignedBoardIssues.length === 1 ? '' : 's'}:
                                </strong>{' '}
                                Assign someone before work can progress.{' '}
                                {unassignedBoardIssues.map((board) => (
                                    <Link
                                        key={board.board_id}
                                        to={`/Graphe/dashboard/kanbanboard/${board.board_id}`}
                                        className="me-3"
                                    >
                                        {board.board_name} ({board.count})
                                    </Link>
                                ))}
                            </div>
                        </Col>
                    )}
                    <Col md={12} lg={12} className="mb-3 px-3">
                        <CardReviewQueue
                            refreshKey={reviewQueueTick}
                            onOpenCard={openReviewCard}
                            onCountChange={setPendingReviewCount}
                        />
                    </Col>
                    <Col md={12} lg={12} className='mb-4 bg-white bordrow py-3 px-0'>
                        <div className="d-flex justify-content-end align-items-center gap-2 sticky-top w-100 bg-white p-2 border-bottom" style={{ top: '-18px', zIndex: '999' }}>
                            <Form.Select
                                size="sm"
                                style={{ width: '180px' }}
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                            >
                                <option value="recent">Most Recently Active</option>
                                <option value="least">Least Recently Active</option>
                                <option value="az">A to Z</option>
                                <option value="za">Z to A</option>
                            </Form.Select>

                            <IconField iconPosition="left" style={{ width: '220px' }}>
                                <InputIcon className="pi pi-search"> </InputIcon>
                                <InputText
                                    placeholder="Search"
                                    className="ps-5"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </IconField>
                            <Button
                                title="Clear Filter"
                                icon="pi pi-refresh"
                                className="border-0 p-0"
                                severity="secondary"
                                text
                                onClick={() => {
                                    setSearchTerm(''); // clear search
                                    setDebouncedSearch(''); // also clear the debounced value immediately

                                }}
                            />

                        </div>
                        <Row className="mx-0">
                            <Col lg={9} md={12} sm={12} className="p-0 px-2">
                                <ul className="bordList">
                                    {loading ? (
                                        <p className="text-center mt-3">Loading boards...</p>
                                    ) : boards.length === 0 ? (
                                        <p className="text-center mt-3">No boards found</p>
                                    ) : (
                                        boards.map((board) => {
                                            const unassignedCount =
                                                unassignedBoardIssues.find(
                                                    (row) => Number(row.board_id) === Number(board.board_id)
                                                )?.count || 0;
                                            return (
                                            <li key={board.board_id}>

                                                <Card className="h-100">
                                                    <Card.Header className="d-flex justify-content-between ">
                                                        <div>
                                                            <h6 className="mb-2">{board.name}</h6>
                                                            <span className="d-block"><small>Brand :</small> {board.brand?.brand_name || 'N/A'}</span>
                                                            <span className="d-block"><small>Project :</small> {board.project?.project_name || 'N/A'}</span>
                                                            {unassignedCount > 0 && (
                                                                <Badge bg="warning" text="dark" className="mt-1">
                                                                    {unassignedCount} unassigned
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <CustomConfirmPopup
                                                            message="Are you sure you want to complete this board?"
                                                            icon="pi pi-exclamation-triangle"
                                                            defaultFocus="accept"
                                                            acceptClassName="p-button-help"
                                                            buttonLabel="Close Board"
                                                            title="Close Board"
                                                            buttonClass="p-button-help p-button-text"
                                                            className="border-0 p-0 clBoard"
                                                            buttonIcon="pi pi-file-check"
                                                            onConfirm={() => handleCloseBoard(board.board_id, true)} // <-- Call API
                                                            onReject={() => console.log('Duplicate action canceled')}
                                                            style={{ width: '35px', zIndex: '1' }}
                                                        />
                                                    </Card.Header>
                                                    <Link
                                                        to={`/Graphe/dashboard/kanbanboard/${board.board_id}`}
                                                        className="d-block"
                                                    >
                                                        <Card.Body style={{ padding: '20px 20px 60px 20px' }}>
                                                            <ol>
                                                                {[...board.lists]
                                                                    .sort((a, b) => a.position - b.position)
                                                                    .map((list, i) => (
                                                                        <li key={i} style={{ color: list.color_code || '#4a00e5' }}>
                                                                            <small>{list.name || 'List'} </small>{list.card_count}
                                                                        </li>
                                                                    ))}
                                                            </ol>
                                                        </Card.Body>
                                                    </Link>
                                                    <Card.Footer className="d-flex py-2 justify-content-between border-top shadow-0" style={{ position: 'absolute', bottom: '0', left: '0', width: '100%', background: 'transparent' }}>
                                                        <span className="d-flex align-items-center gap-2">
                                                            <small>Owner :</small>
                                                            <Avatar
                                                                image={board.owner?.profileImage?.image_url || nouserImg}
                                                                shape="circle"
                                                                id={`owner-avatar-${board.owner?.first_name}-${board.owner?.last_name}`}
                                                            />
                                                            <Tooltip
                                                                target={`#owner-avatar-${board.owner?.first_name}-${board.owner?.last_name}`}
                                                                content={`${board.owner?.first_name || ''} ${board.owner?.last_name || ''}`}
                                                            />
                                                        </span>

                                                        <AvatarGroup>
                                                            {board.members?.map((member, i) => (
                                                                <Avatar
                                                                    key={i}
                                                                    image={member.profileImage?.image_url || nouserImg}
                                                                    shape="circle"
                                                                    className="custom-avatar"
                                                                    data-pr-tooltip={`${member.first_name} ${member.last_name}`}
                                                                />
                                                            ))}
                                                            <Avatar
                                                                icon="pi pi-user-plus"
                                                                shape="circle"
                                                                title="Add Member"
                                                                className="custom-avatar bg-info text-white"
                                                                onClick={(e) => handleAddMember(e, board)}
                                                            />

                                                        </AvatarGroup>
                                                        <Tooltip target=".custom-avatar" />
                                                    </Card.Footer>

                                                </Card>
                                            </li>
                                            );
                                        })
                                    )}
                                </ul>
                            </Col>
                            <Col lg={3} md={12} sm={12} className="border-start">

                                <h6 className=" mt-4">Tasks Requiring Attention</h6>
                                <ul className="Tasklistb">
                                    {userCards.map((card, idx) => (
                                        <li key={card.card_id || idx}>
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                className="d-block"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => openCardDetails(card)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        openCardDetails(card);
                                                    }
                                                }}
                                            >
                                                <Card className={`board-task my-0 
                                                    ${card.missed_deadline ? "task-red example-1" : "task-green"} 
                                                    ${card.priority === 'Urgent' && card.list.name !== 'Completed' ? "example-1" : ""}`}>
                                                    <Card.Header className="d-flex justify-content-end align-items-center gap-2 p-0 border-0">
                                                        <span className={
                                                            card.completedChecklistCount === card.totalChecklistCount && card.totalChecklistCount !== 0
                                                                ? 'text-success'
                                                                : card.completedChecklistCount < card.totalChecklistCount && card.totalChecklistCount !== 0
                                                                    ? 'text-warning'
                                                                    : ''
                                                        }>
                                                            <i className="pi pi-check-square me-1" style={{ fontSize: '12px' }}></i>
                                                            {card.completedChecklistCount || 0}/{card.totalChecklistCount || 0}
                                                        </span>
                                                        <Badge
                                                            className={`text-white badge ${card.priority === "Urgent" || card.priority === "High"
                                                                ? "bg-danger"
                                                                : card.priority === "Medium"
                                                                    ? "bg-warning"
                                                                    : card.priority === "Low"
                                                                        ? "bg-info"
                                                                        : "bg-secondary"
                                                                }`}
                                                        >
                                                            {card.priority}
                                                        </Badge>


                                                        {card.on_hold && (
                                                            <div className="ms-2" style={{ position: "relative" }}>
                                                                <IoHandRightSharp
                                                                    data-pr-tooltip="Task Hold"
                                                                    data-pr-position="top"
                                                                    className="missed-deadline-icon example-1"
                                                                    style={{
                                                                        fontSize: "18px",
                                                                        color: "#f97316",
                                                                        marginRight: "auto",
                                                                    }}
                                                                />
                                                                <Tooltip target=".missed-deadline-icon" />
                                                            </div>
                                                        )}


                                                        {card.missed_deadline && (
                                                            <div className="ms-2" style={{ position: "relative" }}>
                                                                <FaSkullCrossbones
                                                                    data-pr-tooltip="Deadline Missed"
                                                                    data-pr-position="top"
                                                                    className="missed-deadline-icon example-1"
                                                                    style={{
                                                                        fontSize: "18px",
                                                                        color: "#ff63a5",
                                                                        marginRight: "auto",
                                                                    }}
                                                                />
                                                                <Tooltip target=".missed-deadline-icon" />
                                                            </div>
                                                        )}

                                                        {card.priority === 'Urgent' && (
                                                            <div className="ms-2" style={{ position: "relative" }}>
                                                                <CgFlagAlt
                                                                    data-pr-tooltip="Priority Task"
                                                                    data-pr-position="top"
                                                                    className="missed-deadline-icon example-1"
                                                                    style={{
                                                                        fontSize: "18px",
                                                                        color: "#ff63a5",
                                                                        marginRight: "auto",
                                                                    }}
                                                                />
                                                                <Tooltip target=".missed-deadline-icon" />
                                                            </div>
                                                        )}

                                                    </Card.Header>
                                                    <Card.Body className="p-3 pt-1">
                                                        <p className="mb-2">
                                                            <small className="d-block">Task Name :</small>
                                                            <span>{card.title}</span>
                                                        </p>
                                                        <p>
                                                            <small>Task Type :</small>
                                                            <span>{card.task_type || 'Normal'}</span>
                                                        </p>

                                                    </Card.Body>
                                                    <Card.Footer className="d-flex justify-content-between align-items-center border-0 p-3 pt-0">
                                                        <small className={`text-white rounded ${getDueDateClass(card?.due_date)}`} style={{ fontSize: '11px', padding: '2px 5px' }}>
                                                            <i className="pi pi-calendar" style={{ fontSize: '11px' }}></i>
                                                            {" "}{formatDate(card?.createdAt)} - {formatDate(card?.due_date)}
                                                        </small>
                                                        <AvatarGroup>
                                                            {(card?.members || []).length > 0 ? (
                                                                card.members.map((member) => {
                                                                const fullName = `${member.first_name} ${member.last_name}`;

                                                                return (
                                                                    <React.Fragment key={member.id || member.user_id}>
                                                                        <Tooltip target={`#avatar-${member.user_id}`} content={fullName} />
                                                                        <Avatar
                                                                            id={`avatar-${member.user_id}`}
                                                                            image={member?.profileImage?.image_url || 'https://primefaces.org/cdn/primereact/images/avatar/xuxuefeng.png'}
                                                                            shape="circle"
                                                                            title={fullName}
                                                                        />
                                                                    </React.Fragment>
                                                                );
                                                                })
                                                            ) : (
                                                                <Badge bg="warning" text="dark">Unassigned</Badge>
                                                            )}
                                                        </AvatarGroup>
                                                    </Card.Footer>
                                                </Card>
                                            </div>
                                        </li>
                                    ))}
                                </ul>

                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Row>
            <CustomToast ref={toastRef} />

            <Dialog
                visible={taskDialogVisible}
                position="top-right"
                style={{ width: '1000px', margin: '0', borderRadius: '0', height: '100%' }}
                onHide={handleTaskDialogClose}
                draggable={false}
                resizable={false}
                className="KanbanDialog"
            >
                {selectedCardId && (
                    <CardTaskDialogContent
                        cardId={selectedCardId}
                        boardId={selectedBoardId}
                        onClose={handleTaskDialogClose}
                        onReviewAction={handleReviewAction}
                    />
                )}
            </Dialog>

            <OverlayPanel ref={op} dismissable style={{ width: '350px' }}>
                <AddBoardMembersDialog
                    members={selectedBoardData.members}
                    board_id={selectedBoardData.board_id}
                    onSuccess={() => {
                        op.current.hide();
                        fetchBoards();
                    }}
                    onClose={() => op.current.hide()}
                />
            </OverlayPanel>

            <Sidebar header={'Old Boards'} visible={visibleRight} position="right" onHide={() => setVisibleRight(false)} style={{ width: '500px' }}>
                <div className="d-flex justify-content-end gap-2">
                    <Form.Select
                        size="sm"
                        style={{ width: '180px' }}
                        value={closedsortOrder}
                        onChange={(e) => setClosedSortOrder(e.target.value)}
                    >
                        <option value="recent">Most Recently Active</option>
                        <option value="least">Least Recently Active</option>
                        <option value="az">A to Z</option>
                        <option value="za">Z to A</option>
                    </Form.Select>
                    <IconField iconPosition="left" style={{ width: '220px' }}>
                        <InputIcon className="pi pi-search"> </InputIcon>
                        <InputText
                            placeholder="Search"
                            className="ps-5"
                            value={closedsearchTerm}
                            onChange={(e) => setClosedSearchTerm(e.target.value)}
                        />
                    </IconField>
                </div>
                <ul className="bordList">
                    {closedloading ? (
                        <p className="text-center mt-3">Loading boards...</p>
                    ) : closedboards.length === 0 ? (
                        <p className="text-center mt-3">No boards found</p>
                    ) : (
                        closedboards.map((board) => (
                            <li className="w-100" key={board.board_id}>
                                <Card>
                                    <Card.Header className="d-flex justify-content-between ">
                                        <div>
                                            <h6 className="mb-2">{board.name}</h6>
                                            <span className="d-block"><small>Brand :</small> {board.brand?.brand_name || 'N/A'}</span>
                                            <span className="d-block"><small>Project :</small> {board.project?.project_name || 'N/A'}</span>
                                        </div>
                                        <CustomConfirmPopup
                                            message="Are you sure you want to restore this board?"
                                            icon="pi pi-exclamation-triangle"
                                            defaultFocus="accept"
                                            acceptClassName="p-button-danger"
                                            buttonLabel="Restore Board"
                                            title="Restore Board"
                                            buttonClass="p-button-danger p-button-text"
                                            className="border-0 p-0"
                                            buttonIcon="pi pi-undo"
                                            onConfirm={() => handleCloseBoard(board.board_id, false)} // <-- Call API
                                            onReject={() => console.log('Duplicate action canceled')}
                                            style={{ width: '30px' }}
                                        />
                                    </Card.Header>
                                    <Card.Body className="p-3 pb-0">
                                        <ol>
                                            {[...board.lists]
                                                .sort((a, b) => a.list_id - b.list_id)
                                                .map((list, i) => (
                                                    <li key={i} style={{ color: list.color_code || '#4a00e5' }}>
                                                        <small>{list.name || 'List'} </small>{list.card_count}
                                                    </li>
                                                ))}


                                        </ol>
                                    </Card.Body>
                                    <Card.Footer className="d-flex py-2 justify-content-between">
                                        <span className="d-flex align-items-center gap-2">
                                            <small>Owner :</small>
                                            <Avatar
                                                image={board.owner?.profileImage?.image_url}
                                                shape="circle"
                                                id={`owner-avatar-${board.owner?.first_name}-${board.owner?.last_name}`}
                                            />
                                            <Tooltip
                                                target={`#owner-avatar-${board.owner?.first_name}-${board.owner?.last_name}`}
                                                content={`${board.owner?.first_name || ''} ${board.owner?.last_name || ''}`}
                                            />
                                        </span>

                                        <AvatarGroup>
                                            {board.members?.map((member, i) => (
                                                <Avatar
                                                    key={i}
                                                    image={member.profileImage?.image_url}
                                                    shape="circle"
                                                    className="custom-avatar"
                                                    data-pr-tooltip={`${member.first_name} ${member.last_name}`}
                                                />
                                            ))}
                                        </AvatarGroup>
                                        <Tooltip target=".custom-avatar" />
                                    </Card.Footer>
                                </Card>
                            </li>
                        ))
                    )}
                </ul>
            </Sidebar>
        </>
    );
};

export default Boards;